import { createDinero } from '@mr-yum/frontend-core/dist/services/createDinero'
import { RouteLink } from '@mr-yum/frontend-core/dist/services/routes'
import { formatUtcDateTz } from '@mr-yum/frontend-core/dist/support/dates'
import { CenterSpinner, ExternalLinkOutlineIcon } from '@mr-yum/frontend-ui'
import { format, parseISO } from 'date-fns'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import ReactMarkdown from 'react-markdown'
import { CombinedError } from 'urql'

import { DEFAULT_TIMEZONE } from '@/common/constants'
import { NotFound } from '@/components/Common/NotFound'
import { ALLOWED_COMMON_ELEMENTS } from '@/components/Shared/Markdown'
import { OrderingType, PaymentProcessorType } from '@/gql/graphql'
import { reportError } from '@/lib/bugsnag'
import { taxIdNumberLabels } from '@/lib/gstNumberUtils'
import { routes } from '@/lib/routes'
import { taxInvoiceLabels } from '@/lib/taxInvoiceUtils'

import { OrderItems } from './OrderItems'
import {
  CURRENCIES,
  dateFormat,
  displayCompanyList,
  InvoiceLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from './shared'
import { InvoiceProps, InvoiceType } from './types'
import { flattenAdditionalItems, getTotalSurcharge } from './utils'

interface Props extends InvoiceProps {
  fetching?: boolean
  error?: CombinedError | null
}

export const Invoice = ({
  fetching,
  error,
  invoice,
  user,
  items,
  groupedItems,
  totalTaxInCents,
  totalInCents,
  hasNotes = true,
  invoiceType = InvoiceType.BasicInvoice,
}: Props) => {
  if (fetching) {
    return <CenterSpinner />
  }

  if (error) {
    reportError(error)
    return <NotFound className="!w-auto" />
  }

  if (!invoice || !invoice.venue) {
    return <NotFound className="!w-auto" />
  }

  const isChargeToRoom =
    invoice.paymentProcessor === PaymentProcessorType.ChargeToRoom
  const currency = invoice.venue.currency as keyof typeof CURRENCIES
  const displayCompany = displayCompanyList[currency] || displayCompanyList.AUD

  const totalSurchargeInCents = getTotalSurcharge(items)

  return (
    <div className="mx-auto max-w-[500px] text-foreground my-body-md">
      <Table>
        <TableHead>
          <TableHeadCell>
            {isChargeToRoom ? (
              <FormattedMessage defaultMessage="Order summary" id="SB//YQ" />
            ) : (
              taxInvoiceLabels[currency] || (
                <FormattedMessage
                  defaultMessage="Tax invoice & receipt"
                  id="6K9Cjw"
                />
              )
            )}
          </TableHeadCell>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Venue" id="PYmHhr" />
            </TableCell>

            <TableCell className="flex-1 text-right">
              <InvoiceLink href={`https://www.mryum.com/${invoice.venue.slug}`}>
                {invoice.venue.name}
              </InvoiceLink>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Date" id="P7PLVj" />
            </TableCell>

            <TableCell className="flex-1 text-right">
              {formatUtcDateTz(
                invoice.createdAt,
                invoice.venue.timezone || DEFAULT_TIMEZONE,
                dateFormat,
              )}
            </TableCell>
          </TableRow>

          {invoice.tableNumber && (
            <TableRow>
              <TableCell>
                <FormattedMessage defaultMessage="Table number" id="SCjZeO" />
              </TableCell>

              <TableCell className="flex-1 text-right">
                {invoice.tableNumber}
              </TableCell>
            </TableRow>
          )}

          {currency === 'ZAR' && (
            <TableRow>
              <TableCell>
                <FormattedMessage defaultMessage="Invoice" id="Ecfndm" />
              </TableCell>

              <TableCell className="flex-1 text-right">{invoice.id}</TableCell>
            </TableRow>
          )}

          {invoice.batchPosDisplayIds &&
            invoice.batchPosDisplayIds.map((batchPosDisplayId, index) => (
              <TableRow key={index}>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="POS Invoice ID #{number}"
                    id="usYgs8"
                    values={{
                      number: index + 1,
                    }}
                  />
                </TableCell>

                <TableCell className="flex-1 text-right">
                  {batchPosDisplayId}
                </TableCell>
              </TableRow>
            ))}

          {invoice.venue.gstRegistrationNumber && (
            <TableRow>
              <TableCell>
                {taxIdNumberLabels[currency] || (
                  <FormattedMessage
                    defaultMessage="Tax identification number"
                    id="8NphIy"
                  />
                )}
              </TableCell>

              <TableCell className="flex-1 text-right">
                {invoice.venue.gstRegistrationNumber}
              </TableCell>
            </TableRow>
          )}

          <TableRow className="">
            <TableCell isBold>
              <FormattedMessage defaultMessage="Total" id="MJ2jZQ" />
            </TableCell>

            <TableCell className="flex-1 text-right" isBold>
              {createDinero(totalInCents, currency).toFormat()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {(user ||
        invoice.venue.additionalInvoiceInformation ||
        invoice.orderingWindowStartDate) && (
        <Table>
          {user && (
            <TableHead>
              <TableHeadCell>
                {user?.name ?? (
                  <FormattedMessage defaultMessage="Your details" id="HmykEw" />
                )}
              </TableHeadCell>
            </TableHead>
          )}
          <div>
            {user?.email && (
              <TableCell>
                <FormattedMessage defaultMessage="Email:" id="xpTPb3" />{' '}
                {user.email}
              </TableCell>
            )}

            {user?.mobile && (
              <TableCell>
                <FormattedMessage defaultMessage="Phone:" id="r6JIKW" />{' '}
                {user.mobile}
              </TableCell>
            )}

            {invoice.venue.additionalInvoiceInformation && (
              <div>
                <ReactMarkdown
                  unwrapDisallowed
                  allowedElements={ALLOWED_COMMON_ELEMENTS}
                  components={{
                    a: ({ href, children }) => (
                      <InvoiceLink target="_blank" href={href}>
                        {children}
                      </InvoiceLink>
                    ),
                  }}
                >
                  {invoice.venue.additionalInvoiceInformation}
                </ReactMarkdown>
              </div>
            )}

            {invoice.orderingWindowStartDate && (
              <TableCell>
                <FormattedMessage
                  defaultMessage="Ordering window:"
                  id="heD6RA"
                />{' '}
                {format(parseISO(invoice.orderingWindowStartDate), dateFormat)}
              </TableCell>
            )}
          </div>
        </Table>
      )}

      <div data-testid="invoice-line-items">
        {groupedItems.map((group, index) => (
          <Table
            key={index}
            data-testid={
              group.groupTitle?.replace(/^[a-z]/i, '_') ?? 'ungrouped'
            }
          >
            <TableHead>
              <TableHeadCell>
                {group.groupTitle ?? (
                  <FormattedMessage defaultMessage="Your order" id="yLi5uF" />
                )}
              </TableHeadCell>
            </TableHead>

            <TableBody>
              <OrderItems lineItems={group.menuItems} currency={currency} />
              {invoiceType === InvoiceType.MultiVendorOrder && (
                <TableCell className="mt-2 text-left">
                  <RouteLink
                    route={routes.invoice}
                    pathData={{
                      venueSlug: group.groupSlug!,
                      orderId: group.orderId!,
                    }}
                  >
                    <a aria-label="View receipt">
                      View receipt{' '}
                      <ExternalLinkOutlineIcon
                        width={18}
                        height={18}
                        className="inline-block align-sub"
                      />
                    </a>
                  </RouteLink>
                </TableCell>
              )}
            </TableBody>
          </Table>
        ))}
      </div>

      <Table data-testid="invoice-totals">
        <TableBody>
          <OrderItems
            lineItems={flattenAdditionalItems(groupedItems)}
            currency={currency}
          />

          {totalSurchargeInCents !== 0 && (
            <TableRow>
              <TableCell className="flex-1">
                {totalSurchargeInCents > 0 ? (
                  <FormattedMessage defaultMessage="Surcharge" id="29glNz" />
                ) : (
                  <FormattedMessage defaultMessage="Discount" id="28C0Aj" />
                )}
              </TableCell>

              <TableCell className="text-right">
                {createDinero(totalSurchargeInCents, currency).toFormat()}
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell className="flex-1">{displayCompany.taxName}</TableCell>

            <TableCell className="text-right" data-testid="invoice-total-tax">
              {createDinero(totalTaxInCents, currency).toFormat()}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="flex-1">
              <FormattedMessage id="L8seEc" defaultMessage="Subtotal" />
            </TableCell>

            <TableCell className="text-right" data-testid="invoice-subtotal">
              {createDinero(
                totalInCents - totalTaxInCents,
                currency,
              ).toFormat()}
            </TableCell>
          </TableRow>

          <TableRow className="mt-4 my-label-md">
            <TableCell className="flex-1" isBold>
              <FormattedMessage defaultMessage="Total paid" id="F8k2je" />
            </TableCell>

            <TableCell
              className="text-right"
              data-testid="invoice-total"
              isBold
            >
              {createDinero(totalInCents, currency).toFormat()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {hasNotes &&
        (invoice.deliveryOption ||
          invoice.pickupOption ||
          invoice.orderNotes) && (
          <Table>
            <TableHead>
              <TableHeadCell>
                <FormattedMessage defaultMessage="Notes" id="7+Domh" />
              </TableHeadCell>
            </TableHead>

            <div className="text-foreground">
              {invoice?.orderingType &&
                [OrderingType.PickUp, OrderingType.Counter].includes(
                  invoice.orderingType,
                ) &&
                invoice.pickupOption && (
                  <TableCell>
                    <FormattedMessage
                      defaultMessage="Pick-up option:"
                      id="CdcCNf"
                    />{' '}
                    {invoice.pickupOption}
                  </TableCell>
                )}

              {invoice.orderingType === OrderingType.Delivery &&
                invoice.deliveryOption && (
                  <TableCell>
                    <FormattedMessage
                      defaultMessage="Delivery option:"
                      id="VWvhBw"
                    />{' '}
                    {invoice.deliveryOption}
                  </TableCell>
                )}

              {invoice.orderNotes && (
                <TableCell>
                  <FormattedMessage defaultMessage="Order notes:" id="0qZozk" />{' '}
                  {invoice.orderNotes}
                </TableCell>
              )}
            </div>
          </Table>
        )}

      <div className="mb-6 bg-surface p-4 px-4 text-center text-foreground my-body-xs">
        <div className="my-body-sm-bold">
          <FormattedMessage
            description="Company email label"
            defaultMessage="Have questions about your order?"
            id="hT+RxR"
          />
        </div>

        {invoice.venue.supportPhone && (
          <div className="py-2">
            <FormattedMessage
              description="Venue support phone label"
              defaultMessage="Call {venueName} on"
              values={{ venueName: invoice.venue.name }}
              id="qJ3Fg5"
            />{' '}
            <InvoiceLink href={`tel:${invoice.venue.supportPhone}`}>
              {invoice.venue.supportPhone}
            </InvoiceLink>
          </div>
        )}

        <div>
          <FormattedMessage defaultMessage="Or you can" id="0avUG2" />{' '}
          <InvoiceLink
            href={`mailto:${invoice.venue.supportEmail ?? 'hello@mryum.com'}`}
            className="underline"
          >
            <FormattedMessage defaultMessage="reach out by email" id="68NGuT" />
          </InvoiceLink>
        </div>
      </div>

      <div className="w-full text-center text-foreground my-body-xs">
        <div>{displayCompany.name}</div>

        {!invoice.venue.gstRegistrationNumber && currency === 'AUD' && (
          <div>{displayCompany.regNum}</div>
        )}

        <div>
          <FormattedMessage
            description="Company email label"
            defaultMessage="Email:"
            id="T44B7k"
          />{' '}
          {isChargeToRoom ? (
            <InvoiceLink href="mailto:hello@mryum.com">
              hello@mryum.com
            </InvoiceLink>
          ) : (
            <InvoiceLink href={`mailto: ${displayCompany.email}`}>
              {displayCompany.email}
            </InvoiceLink>
          )}
        </div>
      </div>
    </div>
  )
}
